import { useRouter } from "next/router";
import DropdownCustomItem from "@/components/elements/DropdownCustomItem/DropdownCustomItem";
import CustomBadge from "@/components/elements/CustomBadge/CustomBadge";
import { useTranslation } from "next-i18next";
// ARRAYS
import { orderby } from "@/components/core/Classes/OrderBy";
// BOOTSTRAP
import Container from "react-bootstrap/Container";
import Dropdown from "react-bootstrap/Dropdown";
// ICONS
import { SlidersIcons } from "@/public/icons/icons";
// STYLE
import s from "./TopFilters.module.scss";
import CustomDropdownToggle from "@/components/elements/AccordionsToggles/CustomDropdownToggle";
import fallbackTranslation from "@/lib/dbTranslations";

interface TopFiltersProps {
  clothingProductsGroup: any;
  colors: any;
  sizes: any;
}

export default function TopFilters({ clothingProductsGroup, colors, sizes }: TopFiltersProps) {
  const { t } = useTranslation(["common", "db-translations"]);
  const router = useRouter();

  // Remove the selected filter, should pass the type of filter (string) and the name of the item it should remove (string)
  function handleRemoveFilter(filterType, item) {
    let queryToUpdate = router.query[filterType]
      ?.toString()
      .split(",")
      .filter((filter) => {
        return filter !== item;
      });
    router.push({
      pathname: router.pathname,
      query: { ...router.query, [filterType]: queryToUpdate.toString() },
    });
  }

  // Display all the active filters and add an Delete Icon in the top right corner of each one
  function handleActiveFilters(filterType) {
    return [
      !router.query[filterType] ? (
        ""
      ) : (
        <span key={filterType} className="pe-2">
          {t("common." + filterType)}:
        </span>
      ),
      router.query[filterType]
        ? router.query[filterType]
            .toString()
            .split(",")
            .map((item, index) => {
              return (
                <div key={`${filterType}-${index}`} className="p-1">
                  <CustomBadge value={item} type={filterType} handleRemove={handleRemoveFilter} />
                </div>
              );
            })
        : "",
    ];
  }

  return (
    <div>
      <Container fluid className="px-0 d-flex justify-content-between">
        <div className="d-flex">
          {/* Product class filter (small devices) */}
          <Dropdown className={s.filtercategorydropdown}>
            <Dropdown.Toggle
              withChevron={false}
              as={CustomDropdownToggle}
              className="mb-2 pb-0 ps-0 pe-2 shadow-none"
              bootstrapStyles="fw-semibold"
              variant="link"
              id="dropdown-category"
            >
              {t("common.categories").toUpperCase()}
            </Dropdown.Toggle>
            <Dropdown.Menu className={s.menu}>
              {clothingProductsGroup.map(
                (listItem, index) =>
                  listItem.active && (
                    <Dropdown.Item key={index} eventKey={index}>
                      <DropdownCustomItem type="checkbox" filterType="category" redirectPage="catalogue" multipleSelection={false} item={listItem} />
                    </Dropdown.Item>
                  )
              )}
            </Dropdown.Menu>
          </Dropdown>

          {/* Sizes filter */}
          <Dropdown>
            <Dropdown.Toggle
              withChevron={false}
              as={CustomDropdownToggle}
              className="mb-2 pb-0 ps-0 pe-2 shadow-none"
              bootstrapStyles="fw-semibold"
              variant="link"
              id="dropdown-basic"
            >
              {t("common.size").toUpperCase()}
            </Dropdown.Toggle>
            <Dropdown.Menu className={s.menu}>
              {sizes?.map(
                (listItem, index) =>
                  listItem.active && (
                    <Dropdown.Item key={index} eventKey={index}>
                      <DropdownCustomItem type="checkbox" filterType="sizes" redirectPage="catalogue" multipleSelection={true} item={listItem} />
                    </Dropdown.Item>
                  )
              )}
            </Dropdown.Menu>
          </Dropdown>

          {/* Colors filter */}
          <Dropdown>
            <Dropdown.Toggle
              withChevron={false}
              as={CustomDropdownToggle}
              className="mb-2 pb-0 ps-0 pe-2 shadow-none"
              bootstrapStyles="fw-semibold"
              variant="link"
              id="dropdown-basic"
            >
              {t("common.color").toUpperCase()}
            </Dropdown.Toggle>
            <Dropdown.Menu className={s.menu}>
              {colors
                ?.sort((a, b) =>
                  fallbackTranslation("colors", "db-translations", a, t, router.locale).localeCompare(
                    fallbackTranslation("colors", "db-translations", b, t, router.locale)
                  )
                )
                .map(
                  (listItem, index) =>
                    listItem.active && (
                      <Dropdown.Item key={index} eventKey={index}>
                        <DropdownCustomItem type="checkbox" filterType="colors" redirectPage="catalogue" multipleSelection={true} item={listItem} />
                      </Dropdown.Item>
                    )
                )}
            </Dropdown.Menu>
          </Dropdown>
        </div>
        {/* OrderBy filter */}
        <Dropdown>
          <Dropdown.Toggle
            withChevron={false}
            as={CustomDropdownToggle}
            className={`shadow-none fw-semibold ${s.dropdown_toggle}`}
            bootstrapStyles="fw-semibold"
            variant="link"
            id="dropdown-basic"
          >
            <span className="d-none d-sm-inline">
              {router.query?.orderBy
                ? t("order-by." + router.query.orderBy.toString().toLowerCase(), { ns: "common" }).toUpperCase()
                : t("common.order-by").toUpperCase()}
            </span>
            <span className="d-sm-none">
              <SlidersIcons />
            </span>
          </Dropdown.Toggle>
          <Dropdown.Menu className={`${s.orderByMenu} pt-2`}>
            {orderby.map((listItem, index) => (
              <Dropdown.Item key={index} eventKey={index}>
                <DropdownCustomItem
                  key={index}
                  type="radio"
                  filterType="orderBy"
                  redirectPage="catalogue"
                  multipleSelection={false}
                  item={listItem}
                />
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>

        {router.query.category == "category" &&
          fallbackTranslation("clothing-product-group", "db-translations", router.query.category, t, router.locale)}
        {router.query.category == "sizes" && fallbackTranslation("sizes", "db-translations", router.query.category, t, router.locale)}
        {router.query.category == "colors" && fallbackTranslation("colors", "db-translations", router.query.category, t, router.locale)}
        {router.query.category == "orderBy" && t("order-by." + router.query.category.toLowerCase(), { ns: "common" })}
      </Container>
      <div className="pt-2 pb-3">
        <div className="flex-wrap align-items-center d-flex">{handleActiveFilters("sizes")}</div>
        <div className="flex-wrap align-items-center d-flex">{handleActiveFilters("colors")}</div>
      </div>
    </div>
  );
}
